import Controller from '@ember/controller';
import { action } from '@ember/object';
import { alias } from '@ember/object/computed';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';

import { Disclaimer, Spinner, TextField } from '@repo/design-system-kit';
import { VideoAnimations } from '@repo/domain-kit/onboarding';
import { sensitiveAction } from '@repo/sca';
import * as Sentry from '@sentry/ember';
import { dropTask, timeout } from 'ember-concurrency';

import ENV from 'register-partners-js/config/environment';
import { HTTP_STATUS_CODES } from 'register-partners-js/constants/http-status-codes';
import { createEmberZendeskLocalizationAdapter } from 'register-partners-js/utils/zendesk-localization-adapter';

import styles from './styles.module.css';

const THROTTLE_MS = 500;

export default class WelcomeEmailConfirmationController extends Controller {
  spinner = Spinner;
  disclaimerInline = Disclaimer.Inline;
  textField = TextField;
  videoAnimations = VideoAnimations;

  @service session;
  @service router;
  @service toastFlashMessages;
  @service intl;
  @service store;
  @service segment;
  @service networkManager;
  @service userManager;
  @service zendeskLocalization;

  @tracked remoteError;

  @alias('model.user') user;
  @alias('model.redirection') redirection;

  get emailIntroSrc() {
    return `${ENV.videoPath}/register-confirm-email-intro.mp4`;
  }

  get emailFallbackSrc() {
    return '/illustrations/animations/fallbacks/register-email-confirm-fallback.png';
  }

  submitTask = dropTask(async () => {
    let { email, code } = this.user;
    try {
      await sensitiveAction({
        action: () => this.session.authenticate('authenticator:custom', { code, email }),
        onScaSuccess: params => {
          this.networkManager.scaHeader = params;
        },
        hostFaqService: createEmberZendeskLocalizationAdapter(this.zendeskLocalization),
        user: this.userManager.currentUser || {},
      });
      this.remoteError = null;
      this.segment.track('onboarding_partner_email_verified');
      this.store.unloadRecord(this.user);
      this.segment.track('onboarding_partner_credentials_succeeded');
    } catch (error) {
      switch (error.status) {
        case HTTP_STATUS_CODES.UNAUTHORIZED:
          this.remoteError = this.intl.t('confirmation-code.errors.invalid_code');
          break;
        case HTTP_STATUS_CODES.TOO_MANY_REQUESTS:
          this.remoteError = this.intl.t('confirmation-code.errors.flood_email_code');
          break;
        case HTTP_STATUS_CODES.NOT_FOUND:
          // Success! Redirect to consent page
          this.segment.track('onboarding_partner_email_verified');
          this.router.transitionTo('welcome.consent');
          break;
        default:
          // Any other status code
          this.toastFlashMessages.toastError(
            this.intl.t('confirmation-code.errors.internal_error')
          );
          Sentry.captureException(error);
          break;
      }
    }
  });

  requestResendEmailTask = dropTask(async () => {
    await this.user.confirmEmail();

    this.toastFlashMessages.toastSuccess(this.intl.t('toasts.email_confirm_code_sent'));

    await timeout(THROTTLE_MS);
  });

  get isQontoInvoicingFlow() {
    return this.redirection.legalFlow === 'qonto_invoicing';
  }

  get submitBtnClass() {
    if (this.isQontoInvoicingFlow) {
      return `${this.styles['submit-btn']} ${this.styles['full-width']} mb-16`;
    }
    return this.styles['submit-btn'];
  }

  @action
  handleGoBack() {
    this.router.transitionTo('welcome', this.redirection.id);
  }

  get subtitle() {
    if (this.redirection.companyCreationFromScratch) {
      return this.intl.t('confirmation-code.fr-full-cc.subtitle', {
        email_address: this.user.email,
      });
    }
    if (this.redirection.partnerType === 'legal_tech' || this.redirection.partnerType === 'embed') {
      return this.intl.t('confirmation-code.subtitle', {
        email_address: this.user.email,
        legal_tech_partner_name: this.redirection.partnerName,
      });
    }
    if (this.redirection.partnerType === 'accountant') {
      return this.intl.t('confirmation-code.accountant.subtitle', {
        email_address: this.user.email,
      });
    }
  }
  styles = styles;
}
